// src/pages/Login.jsx
import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../api";
import { GoogleLogin } from "@react-oauth/google";


const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setMsg("Verifying...");
    setLoading(true);

    try {
      // API call
      // FIXED: Added /api prefix to ensure correct routing in production
      const res = await api.post("/api/auth/login", { email, password }, { withCredentials: true });


      // STRICT VALIDATION: Ensure we got a real JSON response, not a fallback HTML page
      if (typeof res.data === 'string' && res.data.trim().startsWith('<!DOCTYPE html>')) {
        throw new Error("Server Error: Received HTML instead of JSON. Backend may be down.");
      }

      if (!res.data || !res.data.token) {
        throw new Error("Invalid server response: Missing authentication token.");
      }

      // ✅ Store token using the same key your app checks (authToken)
      //localStorage.setItem("authToken", res.data.token);
      sessionStorage.setItem("authToken", res.data.token);
      sessionStorage.removeItem("adminToken");
      sessionStorage.removeItem("adminUser");
      if (res.data.user) {
        sessionStorage.setItem("user", JSON.stringify(res.data.user));
      }

      // Check for domain param
      const searchParams = new URLSearchParams(location.search);
      const domain = searchParams.get("domain");

      if (domain) {
        try {
          await api.post("/api/domains/add", {
            user_id: res.data.user.id,
            domain_name: domain
          });
          setMsg("✅ Login successful! Domain added. Redirecting...");
          setTimeout(() => navigate("/scans"), 1000);
        } catch (scanErr) {
          console.error("Failed to add domain:", scanErr);
          setMsg("✅ Login successful! (Failed to add domain). Redirecting...");
          setTimeout(() => navigate("/"), 1000);
        }
      } else {
        setMsg("✅ Login successful! Redirecting...");
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || "Login failed. Please check your credentials.";
      setMsg(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-4 d-flex align-items-center">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card shadow border-0">
              <div className="card-body p-4">
                <div className="text-center mb-4">
                  <h2 className="card-title text-primary">
                    <i className="las la-user-shield me-2"></i>
                    Login
                  </h2>
                  <p className="text-muted">User Management</p>
                </div>

                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={showPassword ? "las la-eye-slash" : "las la-eye"}></i>
                      </button>
                    </div>
                  </div>

                  <div className="mb-3 text-end">
                    <Link to="/forgot-password" className="text-decoration-none small text-muted">
                      Forgot Password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 py-2"
                    disabled={loading}
                  >
                    {loading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Signing in...</> : 'Login'}
                  </button>

                  <div className="text-center my-3">
                    <span className="text-muted">OR</span>
                  </div>

                   <div className="d-flex justify-content-center w-100 mb-3">
                    <GoogleLogin
                      onSuccess={async (credentialResponse) => {
                        try {
                          const { credential } = credentialResponse;
                          setMsg("Verifying with Google...");

                          // Send to backend
                          const res = await api.post("/api/auth/google", {
                            token: credential
                          });

                          if (res.data.token) {
                            sessionStorage.setItem("authToken", res.data.token);
                            sessionStorage.removeItem("adminToken");
                            sessionStorage.removeItem("adminUser");
                            if (res.data.user) {
                              sessionStorage.setItem("user", JSON.stringify(res.data.user));
                            }
                            setMsg("✅ Google Login successful! Redirecting...");
                            setTimeout(() => navigate("/"), 1000);
                          }
                        } catch (err) {
                          console.error("Google Login Error:", err);
                          setMsg("❌ Google Login Failed");
                        }
                      }}
                      onError={() => {
                        // Login Failed handled via UI state
                        setMsg("❌ Google Login Failed");
                      }}
                      width="100%"
                    />
                  </div>
                </form>

                {msg && (
                  <p className="text-center mt-3 text-muted fw-semibold">{msg}</p>
                )}

                <div className="text-center mt-3">
                  <Link to="/register" className="text-muted text-decoration-none">
                    <i className="las la-arrow-left me-1"></i>
                    New User? Register here
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Login;
